import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import StructureAPI from '../../../../../redux/structure/StructureAPI';
import {
  BusinessLayerGroupType,
  getTypeFromGroup,
} from '../../../../../redux/structure/util/getTypeFromGroup';
import { useMemo } from 'react';
import { NormalizedGroup } from '../../../../../redux/structure/GroupsAdapter';
import getPathOfGroups from '../../../../../redux/structure/util/getPathOfGroups';
import useAlarmsByGroup from '../../../../../redux/alarm/hooks/useAlarmsByGroup';
import useDeviceStatusFromGroup from '../../../../../service/device/CommanderAPI/hooks/useDeviceStatusFromGroup';
import { getDevicesIdsFromGroupDictionary } from '../../../../../redux/structure/selectors/Group.selector';
import { toBase64 } from '../../../../../shared/util/base64';
import _ from 'lodash';
import orComparator from '../../../../../shared/util/lodash/merge/orComparator';

type Options = Parameters<typeof StructureAPI.useGetStructureV2Query>[1];
type IRoleEntity =
  | 'markets'
  | 'businessUnits'
  | 'regions'
  | 'districts'
  | 'stores'
  | 'devices';
function useTypeEntity(options?: Options) {
  const { groupId = '', marketId } = useParams<{
    groupId: string;
    marketId: string;
  }>();
  const encodedGroupId = toBase64(groupId);

  const { data, ...rest } = StructureAPI.useGetStructureV2Query(
    marketId,
    options as any
  );
  let role: IRoleEntity = 'businessUnits';
  // if path start with markets/ role is markets, but if marketId is present role is businessUnits
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/markets');
  const entity = data?.entities?.groups?.[encodedGroupId];
  const type = getTypeFromGroup(entity, marketId, isAdmin);

  if (type === BusinessLayerGroupType.Markets) {
    role = 'markets';
  } else if (type === BusinessLayerGroupType.BusinessUnits) {
    role = 'businessUnits';
  } else if (type === BusinessLayerGroupType.BusinessUnit) {
    role = 'regions';
  } else if (type === BusinessLayerGroupType.Region) {
    role = 'districts';
  } else if (type === BusinessLayerGroupType.District) {
    role = 'stores';
  } else if (type === BusinessLayerGroupType.Store) {
    role = 'devices';
  }

  const childGroups: NormalizedGroup[] | undefined = useMemo(
    () =>
      !encodedGroupId
        ? (data?.result as string[])
            ?.map((id) => data?.entities?.groups?.[id] as NormalizedGroup)
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            )
        : entity?.childGroups
            ?.map(
              (id) => (data as any)?.entities?.groups?.[id] as NormalizedGroup
            )
            .filter((group) => !group.composableDevice)
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            ),
    [encodedGroupId, entity?.childGroups, data]
  );

  const { data: alarmsData, ...restAlarms } = useAlarmsByGroup({
    groupId,
  });

  const {
    numberFailingMqttConnection,
    data: devicesStatus,
    ...restDeviceStatus
  } = useDeviceStatusFromGroup(groupId);

  const [searchBarParams] = useSearchParams();
  const isTemperatureActive = searchBarParams.get('alarmsActive') === 'true';
  const isCommanderStatusFalling =
    searchBarParams.get('deviceStatusFailing') === 'true';
  const commanderIds =
    data?.entities?.groups &&
    getDevicesIdsFromGroupDictionary(data?.entities?.groups, encodedGroupId);

  const filteredChildGroups = useMemo(
    () =>
      childGroups
        ?.filter((group) => {
          const commanderIds =
            data?.entities?.groups &&
            getDevicesIdsFromGroupDictionary(data?.entities?.groups, group.id);

          if (isTemperatureActive && alarmsData?.length) {
            return alarmsData?.some((alarm) =>
              commanderIds?.includes(alarm.deviceId)
            );
          }
          if (isCommanderStatusFalling && numberFailingMqttConnection) {
            return (
              devicesStatus &&
              commanderIds?.some(
                (commanderId) =>
                  devicesStatus?.entities[commanderId]?.mqttConnected === false
              )
            );
          }
          return true;
        })
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        ),
    [
      childGroups,
      data?.entities?.groups,
      isTemperatureActive,
      alarmsData,
      isCommanderStatusFalling,
      numberFailingMqttConnection,
      devicesStatus,
    ]
  );

  const pathGroups = useMemo(
    () => getPathOfGroups(data?.entities?.groups, encodedGroupId),
    [data?.entities?.groups, encodedGroupId]
  );
  const businessUnit = useMemo(
    () =>
      pathGroups.find((group) => getTypeFromGroup(group) === 'businessUnit'),
    [pathGroups]
  );

  return {
    entity,
    marketId,
    type: getTypeFromGroup(entity, marketId, isAdmin),
    childGroups,
    filteredChildGroups,
    commanderIds,
    groupId,
    encodedGroupId,
    numberFailingMqttConnection,
    devicesStatus,
    alarmsData,
    businessUnit,
    pathGroups,
    role,
    isLoadingGroups: rest.isLoading,
    ..._.chain(rest)
      .mergeWith(restDeviceStatus, orComparator)
      .mergeWith(restAlarms, orComparator)
      .value(),
  };
}

export default useTypeEntity;
